import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PostCard from "@/components/feature/PostCard";
import { categories, posts, topics } from "@/mocks/posts";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") ?? "").trim();
  const [input, setInput] = useState(keyword);

  const results = keyword
    ? posts.filter((post) => {
        const haystack = [post.title, post.excerpt, post.categoryName, ...post.tags]
          .join(" ")
          .toLowerCase();
        return haystack.includes(keyword.toLowerCase());
      })
    : posts;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = input.trim();
    setSearchParams(trimmed ? { q: trimmed } : {});
  };

  return (
    <div className="min-h-screen w-full bg-background-50">
      <SiteHeader />

      <section className="w-full border-b border-background-200/70 bg-background-100">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            {keyword ? `「${keyword}」的搜索结果` : "站内搜索"}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {keyword
              ? `在吃瓜看料基地里共找到 ${results.length} 条相关内容`
              : "输入关键词，搜遍全站热瓜、话题与分类。"}
          </p>

          <form onSubmit={handleSubmit} className="mt-6 flex w-full max-w-xl items-stretch gap-2 rounded-full border border-background-300/70 bg-background-50 p-2">
            <div className="flex flex-1 items-center gap-2 px-3">
              <i className="ri-search-line text-lg text-foreground-500" aria-hidden="true"></i>
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                type="search"
                placeholder="试试搜「顶流」「大厂」「绩效」"
                className="w-full bg-transparent py-1.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
            </div>
            <button
              type="submit"
              className="rounded-full bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600 cursor-pointer"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">热门搜索：</span>
            {topics.slice(0, 6).map((topic) => (
              <Link
                key={topic}
                to={`/search?q=${encodeURIComponent(topic)}`}
                className="rounded-full bg-background-50 border border-background-200/70 px-3 py-1 text-xs text-foreground-700 whitespace-nowrap transition-colors hover:border-secondary-400 hover:text-secondary-700"
              >
                #{topic}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <main className="w-full px-4 md:px-8 py-10 md:py-14">
        {results.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {results.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-10 md:p-16 text-center">
            <i className="ri-search-eye-line text-5xl text-foreground-300" aria-hidden="true"></i>
            <h2 className="mt-4 font-heading text-xl font-bold text-foreground-950">
              没有找到相关的瓜
            </h2>
            <p className="mt-2 text-sm text-foreground-600">换个关键词试试，或者从下面的分类逛逛。</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {categories.map((item) => (
                <Link
                  key={item.slug}
                  to={`/category/${item.slug}`}
                  className="rounded-full bg-background-100 px-4 py-2 text-sm text-foreground-700 whitespace-nowrap transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}